import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SendtooLogo from "@/components/shared/SendtooLogo";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { completePasswordReset } = useAuth();
  const resetToken = location.state?.resetToken;
  const identifier = location.state?.identifier;

  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!resetToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-2">Session Expired</h1>
          <p className="text-slate-500 text-sm mb-6">This password reset link is no longer valid. Please log in again to get a fresh one.</p>
          <Link to="/auth/login" className="text-brand-500 font-semibold hover:underline">Back to Sign In</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (form.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const user = await completePasswordReset(resetToken, form.newPassword, form.confirmPassword);
      toast.success("Password set successfully. Welcome!");
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "agent") navigate("/agent");
      else if (user.role === "staff") navigate("/staff");
      else navigate("/customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password. The link may have expired — try logging in again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex justify-center">
          <SendtooLogo size="lg" align="center" />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-brand-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Set New Password</h1>
          </div>
          <p className="text-slate-500 text-sm mb-6">
            {identifier ? `First login for ${identifier}.` : "First login."} Choose a new password to continue.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">New Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  placeholder="At least 8 characters"
                  required
                  data-testid="reset-new-password"
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm New Password</label>
              <input
                type={showPwd ? "text" : "password"}
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Re-enter your new password"
                required
                data-testid="reset-confirm-password"
                className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2 uppercase tracking-wide"
            >
              {loading ? "Setting Password..." : "Set Password & Continue"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
